import type { EnvVisibility, Platform } from '@hangar/shared';
import type { EnvRequirement } from '@/api/schemas';
import { cn } from '@/lib/utils';
import { Icon } from '@/components/Icon';
import { DefinedInBadge } from '@/components/badges';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { OptionSelect } from '@/components/OptionSelect';
import { PLATFORMS } from './envHelpers';
import { isLocked, normalizeRequirementKey, platformOn, togglePlatform } from './requirementsModel';

interface FieldProps {
  r: EnvRequirement;
  i: number;
  locked: boolean;
  update: (i: number, patch: Partial<EnvRequirement>) => void;
}

interface RowProps {
  r: EnvRequirement;
  i: number;
  keyError: string | undefined;
  update: (i: number, patch: Partial<EnvRequirement>) => void;
  remove: (i: number) => void;
}

/** One rule: key, required, pattern, expected visibility, platforms, origin badge and description. */
export function RequirementRow({ r, i, keyError, update, remove }: RowProps) {
  const locked = isLocked(r);
  return (
    <li data-testid={`env-req-row-${r.key || i}`} className={cn('grid gap-2 rounded-lg border bg-secondary/40 p-3 sm:grid-cols-[minmax(0,1.4fr)_auto_minmax(0,1.4fr)_minmax(0,0.8fr)_auto_auto] sm:items-center', locked && 'opacity-90')}>
      <KeyField r={r} i={i} locked={locked} update={update} keyError={keyError} />
      <label className="flex h-9 items-center gap-2 text-[13px] text-muted-foreground">
        <Checkbox data-testid="env-req-required" checked={r.required} disabled={locked} onCheckedChange={(v) => update(i, { required: v === true })} />
        required
      </label>
      <Input data-testid="env-req-pattern" aria-label="Pattern" value={r.pattern ?? ''} readOnly={locked} onChange={(e) => update(i, { pattern: e.target.value || null })} placeholder="^https://" className="font-mono sm:text-[13px]" spellCheck={false} />
      <VisibilitySelect r={r} i={i} locked={locked} update={update} />
      <PlatformChecks r={r} i={i} locked={locked} update={update} />
      <RowActions r={r} i={i} locked={locked} remove={remove} />
      {r.description || !locked ? (
        <Input data-testid="env-req-description" aria-label="Description" value={r.description ?? ''} readOnly={locked} onChange={(e) => update(i, { description: e.target.value || null })} placeholder="Why this key must exist" className="sm:col-span-6 sm:text-[13px]" />
      ) : null}
    </li>
  );
}

function KeyField({ r, i, locked, update, keyError }: FieldProps & { keyError: string | undefined }) {
  return (
    <div className="flex flex-col gap-1">
      <Input data-testid="env-req-key" aria-label="Key" value={r.key} readOnly={locked} onChange={(e) => update(i, { key: normalizeRequirementKey(e.target.value) })} placeholder="EXPO_PUBLIC_API_URL" className="font-mono sm:text-[13px]" aria-invalid={keyError ? true : undefined} spellCheck={false} />
      {keyError ? <span className="text-xs text-destructive">{keyError}</span> : null}
    </div>
  );
}

const VISIBILITY_OPTIONS = [
  { value: '', label: 'any visibility' },
  { value: 'plain', label: 'plain' },
  { value: 'sensitive', label: 'sensitive' },
  { value: 'secret', label: 'secret' },
];

function VisibilitySelect({ r, i, locked, update }: FieldProps) {
  return (
    <OptionSelect
      data-testid="env-req-visibility"
      aria-label="Expected visibility"
      value={r.expectedVisibility ?? ''}
      disabled={locked}
      onValueChange={(v) => update(i, { expectedVisibility: (v || null) as EnvVisibility | null })}
      options={VISIBILITY_OPTIONS}
    />
  );
}

function PlatformChecks({ r, i, locked, update }: FieldProps) {
  return (
    <div className="flex items-center gap-3">
      {PLATFORMS.map((pl) => (
        <PlatformCheck key={pl} pl={pl} on={platformOn(r.platforms, pl)} locked={locked} onChange={(checked) => update(i, { platforms: togglePlatform(r.platforms, pl, checked) })} />
      ))}
    </div>
  );
}

function PlatformCheck({ pl, on, locked, onChange }: { pl: Platform; on: boolean; locked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <label className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
      <Checkbox data-testid={`env-req-platform-${pl}`} checked={on} disabled={locked} onCheckedChange={(v) => onChange(v === true)} />
      {pl}
    </label>
  );
}

function RowActions({ r, i, locked, remove }: { r: EnvRequirement; i: number; locked: boolean; remove: (i: number) => void }) {
  return (
    <div className="flex items-center justify-end gap-1">
      <DefinedInBadge definedIn={r.definedIn} />
      {locked ? (
        <span className="sr-only">Managed by hangar.yml (HGR-MANAGED-BY-CONFIG)</span>
      ) : (
        <Button variant="ghost" size="icon-sm" aria-label={`Remove ${r.key || 'rule'}`} data-testid="env-req-remove" className="text-subtle hover:text-destructive" onClick={() => remove(i)}>
          <Icon name="delete" size={18} />
        </Button>
      )}
    </div>
  );
}
